import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, Suspense } from 'react';
import { JobForm } from '@/components/jobs';
import { useJob, useUpdateJob } from '@/hooks/useJobs';
import type { JobFormValues } from '@/lib/schemas/job.schema';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export const Route = createFileRoute('/admin/jobs/$id/edit')({
  component: EditJobPage,
});

function EditJobPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: job, isLoading, error } = useJob(id);
  const updateJobMutation = useUpdateJob();
  const [submitError, setSubmitError] = useState<string>();

  const handleSubmit = async (data: JobFormValues) => {
    setSubmitError(undefined);
    try {
      await updateJobMutation.mutateAsync({ id, data: data as any });
      // Navigate back to job details on success
      navigate({ to: '/admin/jobs/$id', params: { id } });
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to update job. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate({ to: '/admin/jobs/$id', params: { id } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={handleCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Edit Job</h1>
          <p className="text-sm text-muted-foreground">
            Update job posting details
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load job. Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Job Form */}
      {job && (
        <Suspense
          fallback={
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          }
        >
          <JobForm
            mode="edit"
            initialData={job}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={updateJobMutation.isPending}
            submitError={submitError}
          />
        </Suspense>
      )}
    </div>
  );
}
